import Minus from "@assets/svg/MyDeliveries/Minus";
import Plus from "@assets/svg/MyDeliveries/Plus";
import Button from "@components/Button";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import color from "@constants/color";
import t from "@constants/localization";
import IssuesImagesUpload from "@screens/MyDeliveries/components/IssuesImagesUpload";
import { itemsQuantityStatusV3 } from "@services/api";
import { getStateDiff } from "@utils/itemMarking";
import toasts from "@utils/toast";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableNativeFeedback,
  View,
} from "react-native";
import OrderItemCard from "../components/CrateCard/OrderItemCard";

const PATH = "screens/CustomerOrderItems";
interface CustomerOrderItemIssuesProps {
  navigation: any;
  route: any;
}

const CustomerOrderItemIssues: React.FC<CustomerOrderItemIssuesProps> = ({
  navigation,
  route,
}) => {
  const { info } = route?.params || {};
  const markingConfig = route?.params?.markingConfig || [];
  const imagesMandatoryForSamasya = route?.params?.imagesMandatoryForSamasya;

  const markingInProcess = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quantities, setQuantities] = useState({
    not_found: 0,
    wrong_item_received: 0,
    item_damaged: 0,
    ofd_db_lost_damage: 0,
    cx_rto_predelivery: 0,
    partial_not_found: 0,
  });

  const [images, setImages] = useState({
    wrong_item_received: [],
    item_damaged: [],
  });

  useEffect(() => {
    if (info) {
      setQuantities({
        not_found: info?.current_state?.not_found,
        wrong_item_received: info?.current_state?.wrong_item_received,
        item_damaged: info?.current_state?.item_damaged,
        ofd_db_lost_damage: info?.current_state?.ofd_db_lost_damage,
        cx_rto_predelivery: info?.current_state?.cx_rto_predelivery,
        partial_not_found: info?.current_state?.partial_not_found,
      });
      setIsLoading(false);
      setImages({
        wrong_item_received:
          info?.wrong_item_received_images?.map((i) => ({ url: i })) || [],
        item_damaged: info?.item_damaged_images?.map((i) => ({ url: i })) || [],
      });
    }
  }, [info]);

  const showAddImageWarning = useMemo(() => {
    return (
      (imagesMandatoryForSamasya?.wrong_item_received &&
        quantities.wrong_item_received > 0 &&
        images?.wrong_item_received.length === 0) ||
      (imagesMandatoryForSamasya?.item_damaged &&
        quantities.item_damaged > 0 &&
        images?.item_damaged.length === 0)
    );
  }, [quantities, images, imagesMandatoryForSamasya]);

  const onImageChange = (issue, issueImages) => {
    if (images[issue]) {
      setImages((p) => ({ ...p, [issue]: issueImages }));
    }
  };

  const handleQuantityChange = (key, change) => {
    const totalQuantity = Object.values(quantities).reduce((a, b) => a + b, 0);
    if (change > 0 && totalQuantity >= info.total_quantity) return;
    setQuantities((prev) => ({ ...prev, [key]: prev[key] + change }));
  };
  const onSubmit = async () => {
    if (showAddImageWarning) {
      toasts.showShortToast("कृप्या फोटो डालें!");
      return;
    }
    if (markingInProcess.current) {
      return;
    }
    markingInProcess.current = true;
    try {
      const new_state = {
        item_damaged: quantities.item_damaged,
        wrong_item_received: quantities.wrong_item_received,
        not_found: quantities.not_found,
        ofd_db_lost_damage: quantities.ofd_db_lost_damage,
        cx_rto_predelivery: quantities.cx_rto_predelivery,
      };

      const payload = {
        order_item_id: info?.order_item_id,
        hash: info?.hash,
        ofd_type: info?.ofd_type,
        type: info?.type,
        diff: getStateDiff(new_state, info?.current_state),
        found_marked: true,
        issue_images: {
          wrong_item_received: images?.wrong_item_received?.map(
            (i) => i?.url && i.url,
          ),
          item_damaged: images?.item_damaged?.map((i) => i?.url && i.url),
        },
      };
      await itemsQuantityStatusV3(payload);
      markingInProcess.current = false;
      navigation.goBack();
    } catch (error) {
      console.log(error);
    } finally {
      markingInProcess.current = false;
    }
  };

  const renderIssueCard = (key, title, view_only = false) => {
    const quantity = quantities[key];

    if (view_only) {
      return (
        <View style={styles.issueContainer}>
          <View style={styles.issueHeader}>
            <Text variant="body_text_4" style={styles.issueText}>
              {title}
            </Text>
            <Text variant="body_text_4" style={styles.issueText}>
              {quantity}
            </Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.issueContainer}>
        <View style={styles.issueHeader}>
          <Text variant="body_text_4" style={styles.issueText}>
            {title}
          </Text>
          {quantity ? (
            <View style={styles.counterContainer}>
              <TouchableNativeFeedback
                onPress={() => handleQuantityChange(key, -1)}
              >
                <View style={styles.iconButton}>
                  <Minus />
                </View>
              </TouchableNativeFeedback>
              <Text variant="title_18" style={styles.counterText}>
                {quantity}
              </Text>
              <TouchableNativeFeedback
                onPress={() => handleQuantityChange(key, 1)}
              >
                <View style={styles.iconButton}>
                  <Plus
                    stroke={
                      quantity < info.total_quantity
                        ? color.white
                        : color.basketBallOrange
                    }
                  />
                </View>
              </TouchableNativeFeedback>
            </View>
          ) : (
            <TouchableNativeFeedback
              onPress={() => handleQuantityChange(key, 1)}
            >
              <View style={styles.addButton}>
                <Plus stroke="rgba(255, 46, 0, 1)" />
                <Text variant="body_text_4" style={styles.addText}>
                  क्वांटिटी
                </Text>
              </View>
            </TouchableNativeFeedback>
          )}
        </View>
        {imagesMandatoryForSamasya?.[key] && quantity ? (
          <IssuesImagesUpload
            key={`${key}_ImagesUpload`}
            reason={key}
            imageRequired={true}
            currentImgs={images[key] || []}
            setUploadedImage={onImageChange}
            info={info}
          />
        ) : null}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        route={route}
        navigation={navigation}
        headertitle={"समस्या है ?"}
        subHeaderTitle={undefined}
        headerStyle={undefined}
        rightIcon={undefined}
        RightButton={undefined}
        goingBackHandler={undefined}
        IsRightIconDropDown={undefined}
        hideShadow={true}
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      ) : (
        <>
          <ScrollView style={{ flex: 1, marginVertical: 16, marginBottom: 70 }}>
            <OrderItemCard
              navigation={navigation}
              info={route?.params?.info}
              showIssueBtn={false}
            />
            <View style={{ paddingHorizontal: 16 }}>
              <Text variant="title_16" style={styles.text}>
                {"कृप्या अपनी समस्या बताएं "}
              </Text>
              {markingConfig?.map((item: any) =>
                renderIssueCard(item.key, item.title, item?.view_only),
              )}
            </View>
          </ScrollView>
          <View style={styles.footer}>
            <Button
              btnText={"आगे बढ़ें"}
              onPress={() => onSubmit()}
              buttonWidth="100%"
              buttonColor={color.plain_green}
              buttonStyle={{ borderRadius: 10 }}
              textStyle={{
                color: color.white,
                textAlign: "center",
                lineHeight: 22,
              }}
              disabled={markingInProcess.current}
            />
          </View>
        </>
      )}
    </View>
  );
};

export default CustomerOrderItemIssues;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  text: {
    marginTop: 12,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: color.white,
  },
  footer: {
    bottom: 0,
    position: "absolute",
    width: "100%",
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: "center",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  issueContainer: {
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.10)",
    backgroundColor: color.white,
    borderRadius: 10,
    marginTop: 12,
    paddingLeft: 16,
    paddingRight: 12,
    paddingVertical: 12,
  },
  issueHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  issueText: { color: "rgba(0, 0, 0, 1)" },
  counterContainer: {
    flexDirection: "row",
    backgroundColor: "rgba(255, 46, 0, 1)",
    borderRadius: 8,
    alignItems: "center",
  },
  counterText: { color: color.white, paddingHorizontal: 3 },
  iconButton: { paddingHorizontal: 13, paddingVertical: 9 },
  addButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(255, 46, 0, 1)",
    paddingHorizontal: 16,
    paddingVertical: 6,
    flexDirection: "row",
    alignItems: "center",
  },
  addText: { color: "rgba(255, 46, 0, 1)", marginLeft: 8 },
});
